/** Call plan goal completion colour model. See docs/features/call-plans.md. */

/** One configured band. `from`/`to` are inclusive percentage bounds; an absent bound is
 *  unbounded on that side, which is how the top band covers any amount of overachievement. */
export interface GoalColorBand {
  readonly from?: number;
  readonly to?: number;
  readonly color: string;
  /** Free text for whoever edits the record, and a translation key if the status word is
   *  ever shown alongside the colour. Never used to match a percentage. */
  readonly label?: string;
}

/** The `indskr_configvalue` payload. */
export interface GoalColorConfig {
  readonly schemaVersion?: number;
  readonly bands?: readonly GoalColorBand[];
}

/** Highest config schema this build understands. Absent in a record means 1. */
export const SUPPORTED_SCHEMA_VERSION = 1;

/**
 * Shipped bands, used when no record is configured. Every value is a colour the app
 * already uses — the theme tokens --omni-custom-orange/-red, and the green the
 * call plan rows have always used for a met goal. Only the two states that did not
 * exist before (past goal, far past goal) needed a colour assigning at all.
 *
 * Nothing is shipped below 100 on purpose: a percentage matching no band takes no colour,
 * so most of a territory stays neutral and the few rows needing attention carry the only
 * colour on screen. A record may still define bands below 100 — those are honoured as
 * tinted text rather than silently ignored.
 */
export const DEFAULT_BANDS: readonly GoalColorBand[] = [
  { from: 100, to: 100, color: '#02C338', label: 'GOAL_ACHIEVED' },
  { from: 101, to: 199, color: '#FF650D', label: 'OVERACHIEVED' },
  { from: 200, color: '#D0021B', label: 'OVERVISITED' },
];

/**
 * The percentage is drawn as coloured text, never as a filled chip: a fill on every row
 * that meets its goal reads as decoration and crowds the list. The configured hue is used
 * directly so an admin sees what they picked; it is only darkened when it would be too
 * faint to read, at which point these control the substitute.
 */
/** WCAG AA for normal text. Below this against white, the configured hue is darkened. */
export const MIN_TEXT_CONTRAST = 4.5;
export const DARKEN_STEP = 0.9;
export const MAX_DARKEN_STEPS = 12;

export interface ResolvedGoalStyle {
  /** The configured hue, as the record states it. Used for the progress bar. */
  readonly fill: string;
  /** The hue as text: `fill` itself, or a darkened form where `fill` is too faint. */
  readonly ink: string;
  readonly label?: string;
}
